import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  SimpleGrid,
  Stack,
  Text,
  useToast
} from "@chakra-ui/react";
import { FaMapMarkerAlt, FaSave, FaSearchLocation, FaUser } from "react-icons/fa";
import { api } from "../api";

const parseLocalUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    return {};
  }
};

const PerfilUsuario = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [foto, setFoto] = useState(null);
  const [avatarVersion, setAvatarVersion] = useState(Date.now());
  const [form, setForm] = useState({
    nome: "",
    email: "",
    endereco: "",
    latitude: "",
    longitude: ""
  });

  const localUser = useMemo(() => parseLocalUser(), []);
  const apiBase = api.defaults.baseURL?.replace(/\/api$/, "") || "";
  const lastGeocodeRef = useRef("");
  const toast = useToast();

  const fotoPreview = useMemo(() => {
    if (!foto) return null;
    return URL.createObjectURL(foto);
  }, [foto]);

  useEffect(() => {
    return () => {
      if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    };
  }, [fotoPreview]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get("/v2/usuarios/me");
        setForm({
          nome: response.data?.nome || "",
          email: response.data?.email || "",
          endereco: response.data?.endereco || "",
          latitude: response.data?.latitude || "",
          longitude: response.data?.longitude || ""
        });
      } catch (error) {
        toast({
          title: "Erro ao carregar perfil",
          status: "error",
          duration: 3000,
          isClosable: true
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [toast]);

  const geocodeEndereco = async (endereco) => {
    const enderecoLimpo = String(endereco || "").trim();
    if (enderecoLimpo.length < 6) return;
    if (lastGeocodeRef.current === enderecoLimpo) return;
    lastGeocodeRef.current = enderecoLimpo;
    setGeocodeLoading(true);

    const key = process.env.REACT_APP_GOOGLE_MAPS_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

    try {
      if (key) {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            enderecoLimpo
          )}&key=${key}`
        );
        const data = await response.json();
        if (data.status === "OK" && data.results?.length) {
          const { lat, lng } = data.results[0].geometry.location;
          setForm((prev) => ({
            ...prev,
            latitude: Number(lat).toFixed(6),
            longitude: Number(lng).toFixed(6)
          }));
          return;
        }
      }

      const fallback = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(enderecoLimpo)}`
      );
      const fallbackData = await fallback.json();
      if (fallbackData?.length) {
        setForm((prev) => ({
          ...prev,
          latitude: Number(fallbackData[0].lat).toFixed(6),
          longitude: Number(fallbackData[0].lon).toFixed(6)
        }));
        return;
      }

      toast({
        title: "Endereco nao encontrado",
        status: "warning",
        duration: 2500,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: "Erro ao buscar coordenadas",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setGeocodeLoading(false);
    }
  };

  const handleFotoChange = (event) => {
    setFoto(event.target.files?.[0] || null);
  };

  const handleSave = async () => {
    if (!localUser?.id) return;
    setSaving(true);
    try {
      await api.put("/v2/usuarios/me", {
        nome: form.nome,
        email: form.email,
        endereco: form.endereco,
        latitude: form.latitude || null,
        longitude: form.longitude || null
      });

      if (foto) {
        const upload = new FormData();
        upload.append("foto", foto);
        await api.post(`/usuarios/upload-foto/${localUser.id}`, upload, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setFoto(null);
        setAvatarVersion(Date.now());
      }

      const currentUser = parseLocalUser();
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...currentUser,
          nome: form.nome,
          email: form.email
        })
      );

      toast({
        title: "Perfil atualizado",
        status: "success",
        duration: 2500,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: "Erro ao salvar perfil",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box px={{ base: 3, md: 5 }} py={{ base: 3, md: 4 }} className="profile-page">
      <Box className="profile-shell">
        <Stack spacing={4}>
          <HStack spacing={3} align="center">
            <Box className="profile-title-icon">
              <FaUser />
            </Box>
            <Stack spacing={0}>
              <Text className="profile-title">Meu Perfil</Text>
              <Text className="profile-subtitle">Atualize dados, localizacao e foto de avatar</Text>
            </Stack>
          </HStack>

          <Box className="profile-avatar-card">
            <Stack align="center" spacing={2.5}>
              <Image
                src={`${apiBase}/usuarios/${localUser?.id}.png?v=${avatarVersion}`}
                alt="Avatar"
                boxSize={{ base: "92px", md: "108px" }}
                borderRadius="full"
                className="profile-avatar"
              />
              <FormControl maxW="320px">
                <FormLabel className="profile-field-label" textAlign="center">
                  Trocar foto do avatar
                </FormLabel>
                <Input
                  className="profile-input profile-file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFotoChange}
                />
              </FormControl>
              {fotoPreview ? (
                <Image
                  src={fotoPreview}
                  alt="Preview"
                  boxSize="72px"
                  borderRadius="full"
                  border="2px solid rgba(200,255,241,0.6)"
                />
              ) : null}
            </Stack>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <FormControl isRequired isDisabled={loading}>
              <FormLabel className="profile-field-label">Nome</FormLabel>
              <Input
                className="profile-input"
                value={form.nome}
                onChange={(event) => setForm((prev) => ({ ...prev, nome: event.target.value }))}
              />
            </FormControl>
            <FormControl isRequired isDisabled={loading}>
              <FormLabel className="profile-field-label">Email</FormLabel>
              <Input
                className="profile-input"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              />
            </FormControl>
          </SimpleGrid>

          <Box className="profile-location-card">
            <HStack spacing={2} mb={2}>
              <Box color="#baf9ea">
                <FaMapMarkerAlt />
              </Box>
              <Text className="profile-section-title">Localizacao</Text>
            </HStack>

            <Stack spacing={3}>
              <FormControl isDisabled={loading}>
                <FormLabel className="profile-field-label">Endereco</FormLabel>
                <Input
                  className="profile-input"
                  value={form.endereco}
                  onChange={(event) => setForm((prev) => ({ ...prev, endereco: event.target.value }))}
                  onBlur={() => geocodeEndereco(form.endereco)}
                />
                <Button
                  mt={2}
                  size="sm"
                  leftIcon={<FaSearchLocation />}
                  className="profile-secondary-btn"
                  isLoading={geocodeLoading}
                  onClick={() => geocodeEndereco(form.endereco)}
                >
                  Buscar coordenadas
                </Button>
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl isDisabled={loading}>
                  <FormLabel className="profile-field-label">Latitude</FormLabel>
                  <Input
                    className="profile-input"
                    value={form.latitude}
                    onChange={(event) => setForm((prev) => ({ ...prev, latitude: event.target.value }))}
                  />
                </FormControl>
                <FormControl isDisabled={loading}>
                  <FormLabel className="profile-field-label">Longitude</FormLabel>
                  <Input
                    className="profile-input"
                    value={form.longitude}
                    onChange={(event) => setForm((prev) => ({ ...prev, longitude: event.target.value }))}
                  />
                </FormControl>
              </SimpleGrid>
            </Stack>
          </Box>

          <Button
            leftIcon={<FaSave />}
            className="profile-save-btn"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={loading}
          >
            Salvar alteracoes
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default PerfilUsuario;
